import { Component } from "@theme/component";

export class SwitchColorComponent extends Component {
  requiredRefs = [];
  #preloadedData = null;

  /** Debug: activer avec localStorage.setItem('debugSwitchColor', '1') puis recharger */
  #debug(...args) {
    try {
      if (window?.localStorage?.getItem("debugSwitchColor") === "1") {
        console.log("[SwitchColor]", ...args);
      }
    } catch (e) {
      // ignore
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // Charger les données préchargées depuis le script JSON
    this.#loadPreloadedData();

    // Fallback: délégation si le thème n'appelle pas on:click (ex. script chargé après component.js)
    this.addEventListener("click", (event) => {
      const swatch = event.target.closest(".color-swatch");
      if (swatch) {
        event.preventDefault();
        event.stopPropagation();
        this.handleSwatchClick(event);
      }
    });

    const swatches = this.querySelectorAll(".color-swatch");
    this.#debug("connected, swatches:", swatches.length, "productId:", this.dataset.productId);
  }

  /**
   * Charge les données préchargées depuis le script JSON dans le DOM
   */
  #loadPreloadedData() {
    const productCard = this.closest(".product-card") || this.closest(".collection__product-card") || this.closest("component-product-card");
    if (!productCard) {
      console.warn("Product card not found for preloaded data");
      return;
    }

    const productId = this.dataset.productId;

    // Chercher le script dans plusieurs endroits possibles
    // 1. Dans le composant lui-même
    let dataScript = this.querySelector(`script[data-product-switch-data="${productId}"]`);

    // 2. Dans le parent du composant (product-switch-color-container)
    if (!dataScript) {
      const parent = this.parentElement;
      if (parent) {
        dataScript = parent.querySelector(`script[data-product-switch-data="${productId}"]`);
      }
    }

    // 3. Dans la product card
    if (!dataScript) {
      dataScript = productCard.querySelector(`script[data-product-switch-data="${productId}"]`);
    }

    // 4. Chercher n'importe quel script avec data-product-switch-data (fallback)
    if (!dataScript) {
      dataScript = productCard.querySelector("script[data-product-switch-data]") || this.querySelector("script[data-product-switch-data]") || (this.parentElement && this.parentElement.querySelector("script[data-product-switch-data]"));
    }

    if (dataScript) {
      try {
        this.#preloadedData = JSON.parse(dataScript.textContent);
        this.#debug("Preloaded data loaded", productId, this.#preloadedData?.switchType);
      } catch (error) {
        console.error("[SwitchColor] Error parsing preloaded data:", error);
      }
    } else {
      this.#debug("No preloaded data script for product", productId);
    }
  }

  /**
   * Indique si la slide est l'image custom "collection product card" (metafield image_collection_product_card).
   * Ne doit pas être écrasée lors du switch de couleur.
   */
  #isCustomFirstSlide(slide) {
    return slide?.querySelector?.('[data-media-id="custom-first"]') != null;
  }

  /**
   * Remet le slideshow d'une gallery à la première slide (sans animation).
   * À appeler après chaque mise à jour de gallery lors d'un switch de couleur.
   */
  #resetGalleryToFirstSlide(gallery) {
    const scroller = gallery?.querySelector('slideshow-slides');
    if (scroller) scroller.scrollLeft = 0;
  }

  /**
   * Crée une slide DOM pour la galerie (quand le produit cible a plus d'images que la carte actuelle).
   * @param {string} imageSrc - URL de l'image (peut être protocol-relative //)
   * @param {string|null} alt - Texte alt
   * @param {number} index - Index de la slide (0 = eager loading)
   * @returns {HTMLElement|null} Élément slideshow-slide ou null si createElement échoue
   */
  #createSlideElement(imageSrc, alt, index) {
    const slide = document.createElement("slideshow-slide");
    const src = imageSrc.startsWith("//") ? `https:${imageSrc}` : imageSrc;
    slide.setAttribute("aria-hidden", index === 0 ? "false" : "true");
    slide.setAttribute("style", `view-timeline-name: --slide-${index}; --product-media-fit: cover;`);
    const div = document.createElement("div");
    div.className = "product-media-new product-media-container media-fit product-media-container--image";
    div.setAttribute("style", "--ratio: 0.74;");
    const img = document.createElement("img");
    img.src = src;
    img.srcset = `${src} 1x`;
    img.loading = index === 0 ? "eager" : "lazy";
    img.alt = alt != null ? String(alt) : "";
    img.className = "product-media__image";
    div.appendChild(img);
    slide.appendChild(div);
    return slide;
  }

  /**
   * Appelé au clic sur un swatch (via on:click du thème ou fallback addEventListener).
   * @param {Event} event - event.target est le bouton .color-swatch (proxy du thème ou cible réelle)
   */
  async handleSwatchClick(event) {
    const swatch = event.target?.closest?.(".color-swatch") || event.target;
    if (!swatch || !swatch.classList?.contains?.("color-swatch")) {
      this.#debug("handleSwatchClick: swatch invalide", event.target);
      return;
    }

    this.#debug("handleSwatchClick", swatch);

    if (swatch.classList.contains("selected")) {
      this.#debug("déjà sélectionné, ignore");
      return;
    }

    const switchType = this.dataset.switchType;
    this.#debug("switchType:", switchType, "swatch data:", {
      variantId: swatch.dataset.variantId,
      productHandle: swatch.dataset.productHandle,
      productId: swatch.dataset.productId,
    });
    const productCard = this.closest(".product-card") || this.closest(".collection__product-card");

    if (!productCard) {
      console.error("[SwitchColor] Product card introuvable (closest .product-card)", this);
      return;
    }

    this.#debug("productCard trouvée, lance switch...");

    // Ajouter une classe de chargement
    productCard.classList.add("switching");

    try {
      if (switchType === "variant") {
        await this.switchVariant(swatch, productCard);
      } else {
        await this.switchProduct(swatch, productCard);
      }

      // Mettre à jour l'état sélectionné
      this.querySelectorAll(".color-swatch").forEach((s) => s.classList.remove("selected"));
      swatch.classList.add("selected");
    } catch (error) {
      console.error("[SwitchColor] Erreur lors du switch:", error);
    } finally {
      productCard.classList.remove("switching");
    }
  }

  async switchVariant(swatch, productCard) {
    const variantId = swatch.dataset.variantId;
    const productHandle = swatch.dataset.productHandle;

    if (!variantId || !productHandle) return;

    // Utiliser les données préchargées si disponibles, sinon faire une requête
    let productData;
    if (this.#preloadedData && this.#preloadedData.switchType === "variant") {
      console.log("Using preloaded data for variant switch");
      productData = {
        variants: this.#preloadedData.variants,
        options: this.#preloadedData.options,
        images: this.#preloadedData.images,
        id: this.#preloadedData.productId,
        handle: this.#preloadedData.productHandle,
      };
    } else {
      console.log("Fetching data from API (no preloaded data)");
      // Fallback: récupérer les données du variant depuis l'API Shopify
      const productUrl = `/products/${productHandle}.js`;
      const response = await fetch(productUrl);
      if (!response.ok) {
        throw new Error(`Erreur lors de la récupération du produit: ${response.status}`);
      }
      productData = await response.json();
    }

    // Trouver le variant sélectionné
    const selectedVariant = productData.variants.find((v) => v.id.toString() === variantId);
    if (!selectedVariant) {
      console.error("Variant non trouvé");
      return;
    }

    // Debug: vérifier la structure du variant et de ses images
    console.log("Selected variant:", {
      id: selectedVariant.id,
      featured_image: selectedVariant.featured_image,
      image: selectedVariant.image,
      featured_media: selectedVariant.featured_media,
    });

    // Debug: vérifier la structure des options
    console.log("Product options:", productData.options, "Type:", typeof productData.options);

    // Trouver l'index de l'option de couleur dans les options du produit
    const colorOptionIndex = this.findColorOptionIndex(productData.options);
    console.log("Color option index:", colorOptionIndex);

    // Trouver l'index de l'option de taille
    const sizeOptionIndex = this.findSizeOptionIndex(productData.options);
    console.log("Size option index:", sizeOptionIndex);

    // Filtrer les variants qui ont la même couleur mais des tailles différentes
    const colorValue = selectedVariant.options[colorOptionIndex];
    const variantsForColor = productData.variants.filter((v) => v.options[colorOptionIndex] === colorValue);

    // Mettre à jour les éléments de la card
    await this.updateCardElements(productCard, {
      variant: selectedVariant,
      product: productData,
      productHandle: productHandle,
      variantsForColor: variantsForColor,
      sizeOptionIndex: sizeOptionIndex,
    });
  }

  /**
   * Trouve l'index de l'option de couleur dans les options du produit
   */
  findColorOptionIndex(options) {
    if (!options || !Array.isArray(options)) return 0;

    for (let i = 0; i < options.length; i++) {
      // Gérer les deux cas : chaîne ou objet avec propriété name
      let optionName = "";
      let optionNameOriginal = "";
      if (typeof options[i] === "string") {
        optionName = options[i].toLowerCase();
        optionNameOriginal = options[i];
      } else if (options[i] && typeof options[i] === "object" && options[i].name) {
        optionName = options[i].name.toLowerCase();
        optionNameOriginal = options[i].name;
      } else {
        continue;
      }

      // Priorité 1: Chercher exactement "CHOISISSEZ VOTRE COULEUR"
      if (optionNameOriginal === "CHOISISSEZ VOTRE COULEUR" || optionName === "choisissez votre couleur") {
        return i;
      }

      // Priorité 2: Chercher les variantes
      if (optionName.includes("couleur") || optionName.includes("color")) {
        return i;
      }
    }
    // Par défaut, on suppose que la couleur est la première option
    return 0;
  }

  /**
   * Trouve l'index de l'option de taille dans les options du produit
   */
  findSizeOptionIndex(options) {
    if (!options || !Array.isArray(options)) return -1;

    for (let i = 0; i < options.length; i++) {
      // Gérer les deux cas : chaîne ou objet avec propriété name
      let optionName = "";
      if (typeof options[i] === "string") {
        optionName = options[i].toLowerCase();
      } else if (options[i] && typeof options[i] === "object" && options[i].name) {
        optionName = options[i].name.toLowerCase();
      } else {
        continue;
      }

      if (optionName.includes("taille") || optionName.includes("size")) {
        return i;
      }
    }
    // Si pas trouvé, retourner le dernier index (généralement la taille)
    return options.length > 1 ? options.length - 1 : -1;
  }

  async switchProduct(swatch, productCard) {
    const productHandle = swatch.dataset.productHandle;
    const productId = swatch.dataset.productId;

    if (!productHandle || !productId) {
      console.error("Product handle ou ID manquant");
      return;
    }

    try {
      // Utiliser les données préchargées si disponibles
      if (this.#preloadedData && this.#preloadedData.switchType === "complementary" && this.#preloadedData.complementaryProducts) {
        console.log("Using preloaded data for complementary product switch");
        const complementaryProduct = this.#preloadedData.complementaryProducts.find((p) => p.id.toString() === productId.toString() || p.handle === productHandle);

        if (complementaryProduct) {
          await this.updateCardFromPreloadedData(productCard, complementaryProduct);
          return;
        }
      }

      // Fallback: utiliser updateCardFromJSON
      console.log("Using JSON fallback for complementary product");
      await this.updateCardFromJSON(productCard, productHandle, productId);
    } catch (error) {
      console.error("Erreur lors du switch de produit:", error);
    }
  }

  /**
   * Met à jour la card avec les données préchargées d'un produit complémentaire
   */
  async updateCardFromPreloadedData(productCard, productData) {
    // Vérifier si le produit est disponible (au moins un variant disponible)
    const isAvailable = productData.variants && productData.variants.some((v) => v.available === true);

    // Ajouter la disponibilité calculée aux données
    productData.available = isAvailable;

    const selectedVariant = productData.variants.find((v) => v.available) || productData.variants[0];

    if (!selectedVariant) {
      console.error("Aucun variant disponible");
      return;
    }

    // Mettre à jour le titre
    const titleLink = productCard.querySelector(".product-card__title");
    if (titleLink) {
      const titleText = titleLink.querySelector("h2");
      if (titleText) {
        titleText.textContent = productData.title;
      }
      titleLink.href = `/products/${productData.handle}`;
    }

    // Mettre à jour le prix
    const priceContainer = productCard.querySelector(".product-card__price");
    if (priceContainer && selectedVariant.price) {
      const formattedPrice = this.formatMoney(selectedVariant.price);
      const priceEl = priceContainer.querySelector(".price");
      priceContainer.querySelectorAll(".price-item--regular, .price__sale .price-item").forEach((el) => {
        el.textContent = formattedPrice;
      });

      const hasCompare = selectedVariant.compare_at_price && Number(selectedVariant.compare_at_price) > Number(selectedVariant.price);
      if (priceEl) {
        if (hasCompare) {
          priceEl.classList.add("price--on-sale");
          const compareEl = priceContainer.querySelector(".compare-at-price");
          if (compareEl) {
            compareEl.textContent = this.formatMoney(selectedVariant.compare_at_price);
            compareEl.style.display = "";
          }
        } else {
          priceEl.classList.remove("price--on-sale");
          const compareEl = priceContainer.querySelector(".compare-at-price");
          if (compareEl) compareEl.style.display = "none";
        }
      }
    }

    // Mettre à jour les images - utiliser la même logique que updateCardFromJSON
    const gallery = productCard.querySelector(".card-gallery");
    if (gallery && productData.images && productData.images.length > 0) {
      console.log("Updating gallery with", productData.images.length, "images from preloaded data");

      const slideshowSlides = gallery.querySelector("slideshow-slides");
      if (slideshowSlides) {
        let allSlides = slideshowSlides.querySelectorAll("slideshow-slide");
        const firstSlideIsCustom = allSlides[0] && this.#isCustomFirstSlide(allSlides[0]);
        const productSlideOffset = firstSlideIsCustom ? 1 : 0;
        const templateSlide = Array.from(allSlides).find((s) => !this.#isCustomFirstSlide(s) && s.querySelector("img")) || allSlides[productSlideOffset];

        const normalizeUrl = (url) => {
          if (!url) return "";
          return url.split("?")[0].split("#")[0];
        };

        productData.images.forEach((imageData, index) => {
          const imageSrc = imageData.src || imageData;
          const imageSrcNormalized = normalizeUrl(imageSrc);

          let slide = Array.from(allSlides).find((s) => {
            if (this.#isCustomFirstSlide(s)) return false;
            const img = s.querySelector("img");
            if (!img) return false;
            const currentSrc = img.src || img.getAttribute("src") || "";
            return normalizeUrl(currentSrc) === imageSrcNormalized;
          });

          if (!slide && productSlideOffset + index < allSlides.length) {
            slide = allSlides[productSlideOffset + index];
          }

          // S'il y a plus d'images que de slides (ex: variante avec 3 images, carte en a 2), créer une nouvelle slide
          if (!slide && templateSlide) {
            slide = this.#createSlideElement(imageSrc, imageData.alt, index);
            if (slide) {
              slide.setAttribute("slide-id", `switch-clone-${productSlideOffset + index}`);
              slide.setAttribute("ref", "slides[]");
              if (templateSlide.getAttribute("class")) slide.setAttribute("class", templateSlide.getAttribute("class"));
              slideshowSlides.appendChild(slide);
              allSlides = slideshowSlides.querySelectorAll("slideshow-slide");
            }
          }

          if (slide) {
            const img = slide.querySelector("img");
            if (img) {
              const currentSrc = img.src || img.getAttribute("src") || "";
              const currentNormalized = normalizeUrl(currentSrc);

              if (currentNormalized !== imageSrcNormalized) {
                img.src = imageSrc;
                img.srcset = `${imageSrc} 1x`;
                img.loading = index === 0 ? "eager" : "lazy";
                img.setAttribute("src", imageSrc);

                const picture = slide.querySelector("picture");
                if (picture) {
                  const sources = picture.querySelectorAll("source");
                  sources.forEach((source) => {
                    if (source.srcset) {
                      source.srcset = source.srcset.replace(/https?:\/\/[^\s,]+/g, imageSrc);
                    }
                  });
                }

                if (img.hasAttribute("data-src")) {
                  img.setAttribute("data-src", imageSrc);
                }
              }
            }

            slide.removeAttribute("hidden");
            slide.setAttribute("aria-hidden", index === 0 ? "false" : "true");
          } else if (index === 0 && !firstSlideIsCustom && allSlides.length > 0) {
              const firstSlide = allSlides[0];
              const img = firstSlide.querySelector("img");
              if (img) {
                img.src = imageSrc;
                img.srcset = `${imageSrc} 1x`;
                img.loading = "eager";
                img.setAttribute("src", imageSrc);

                firstSlide.removeAttribute("hidden");
                firstSlide.setAttribute("aria-hidden", "false");
              }
            }
        });

        if (firstSlideIsCustom) {
          allSlides[0].removeAttribute("hidden");
          allSlides[0].setAttribute("aria-hidden", "false");
        }

        allSlides = slideshowSlides.querySelectorAll("slideshow-slide");
        // Cacher les slides qui ne correspondent à aucune image
        const hideFromIndex = productSlideOffset + productData.images.length;
        allSlides.forEach((slide, index) => {
          if (index >= hideFromIndex) {
            slide.setAttribute("hidden", "");
            slide.setAttribute("aria-hidden", "true");
          }
        });

        // Compter les slides visibles et masquer/afficher les flèches en conséquence
        const visibleSlides = Array.from(allSlides).filter((slide) => !slide.hasAttribute("hidden"));
        const slideshow = gallery.querySelector("slideshow-component");
        const slideshowArrows = slideshow?.querySelector("slideshow-arrows");

        if (slideshowArrows) {
          if (visibleSlides.length <= 1) {
            // Masquer les flèches s'il n'y a qu'une seule slide visible
            slideshowArrows.style.display = "none";
          } else {
            // Afficher les flèches s'il y a plusieurs slides visibles
            slideshowArrows.style.display = "";
          }
        }

        // Forcer le slideshow à mettre à jour ses refs si des slides ont été ajoutées
        if (slideshow && typeof slideshow.updatedCallback === "function") {
          slideshow.updatedCallback();
        }

        this.#resetGalleryToFirstSlide(gallery);
      }
    }

    // Mettre à jour le quick-add
    const quickAdd = productCard.querySelector("quick-add-component");
    if (quickAdd) {
      const variantInput = quickAdd.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = selectedVariant.id;
      }

      if (typeof quickAdd.updateVariants === "function" && productData.variants.length > 1) {
        const sizeOptionIndex = this.findSizeOptionIndex(productData.options);
        if (sizeOptionIndex >= 0) {
          quickAdd.updateVariants(productData.variants, selectedVariant.id, sizeOptionIndex);
        }
      }
    }

    // Mettre à jour la visibilité du quick-add selon la disponibilité (ne pas toucher à la product-card)
    try {
      if (quickAdd) quickAdd.setAttribute("data-available", isAvailable ? "true" : "false");
    } catch (e) {
      // Ne pas faire échouer le switch si l'attribut pose problème
    }

    // Mettre à jour les badges
    this.updateBadges(productCard, productData);
  }

  /**
   * Met à jour la card à partir des données JSON (fallback)
   */
  async updateCardFromJSON(productCard, productHandle, productId) {
    try {
      // Récupérer les données du produit depuis l'API Shopify
      const productUrl = `/products/${productHandle}.js`;
      const response = await fetch(productUrl);

      if (!response.ok) {
        throw new Error(`Erreur lors de la récupération du produit: ${response.status}`);
      }

      const productData = await response.json();
      const selectedVariant = productData.variants.find((v) => v.available) || productData.variants[0];

      if (!selectedVariant) {
        console.error("Aucun variant disponible");
        return;
      }

      // Mettre à jour le titre
      const titleLink = productCard.querySelector(".product-card__title");
      if (titleLink) {
        const titleText = titleLink.querySelector("h2");
        if (titleText) {
          titleText.textContent = productData.title;
        }
        titleLink.href = `/products/${productHandle}`;
      }

      // Mettre à jour le prix
      const priceContainer = productCard.querySelector(".product-card__price");
      if (priceContainer && selectedVariant.price) {
        const formattedPrice = this.formatMoney(selectedVariant.price);
        priceContainer.querySelectorAll(".price-item--regular, .price__sale .price-item").forEach((el) => {
          el.textContent = formattedPrice;
        });

        const hasCompare = selectedVariant.compare_at_price && Number(selectedVariant.compare_at_price) > Number(selectedVariant.price);
        const priceEl = priceContainer.querySelector(".price");
        if (priceEl) {
          if (hasCompare) {
            priceEl.classList.add("price--on-sale");
            const compareEl = priceContainer.querySelector(".compare-at-price");
            if (compareEl) {
              compareEl.textContent = this.formatMoney(selectedVariant.compare_at_price);
              compareEl.style.display = "";
            }
          } else {
            priceEl.classList.remove("price--on-sale");
            const compareEl = priceContainer.querySelector(".compare-at-price");
            if (compareEl) compareEl.style.display = "none";
          }
        }
      }

      // Mettre à jour les images - Pour use_complementary_switch : remplacer toutes les images
      const gallery = productCard.querySelector(".card-gallery");
      if (gallery && productData.images && productData.images.length > 0) {
        const slideshowSlides = gallery.querySelector("slideshow-slides");
        if (slideshowSlides) {
          let allSlides = slideshowSlides.querySelectorAll("slideshow-slide");
          // Utiliser une slide qui contient un <img> comme modèle (la 1re peut être "custom" sans img standard)
          const templateSlide = Array.from(allSlides).find((s) => s.querySelector("img")) || allSlides[0];

          productData.images.forEach((imageData, index) => {
            const imageSrc = imageData.src || imageData;
            let slide = allSlides[index];

            // S'il y a plus d'images que de slides (ex: produit lié a 3 images, carte en a 2), créer une nouvelle slide
            if (!slide) {
              slide = this.#createSlideElement(imageSrc, imageData.alt, index);
              if (slide) {
                slide.setAttribute("slide-id", `switch-clone-${index}`);
                slide.setAttribute("ref", "slides[]");
                if (templateSlide?.getAttribute("class")) {
                  slide.setAttribute("class", templateSlide.getAttribute("class"));
                }
                slideshowSlides.appendChild(slide);
                allSlides = slideshowSlides.querySelectorAll("slideshow-slide");
              }
            }

            if (!slide) return;

            const img = slide.querySelector("img");
            if (img) {
              img.src = imageSrc;
              img.srcset = `${imageSrc} 1x`;
              img.loading = index === 0 ? "eager" : "lazy";
              img.setAttribute("src", imageSrc);
              if (imageData.alt != null) {
                img.setAttribute("alt", imageData.alt);
              }
              const picture = slide.querySelector("picture");
              if (picture) {
                const sources = picture.querySelectorAll("source");
                sources.forEach((source) => {
                  if (source.srcset) {
                    source.srcset = source.srcset.replace(/https?:\/\/[^\s,]+/g, imageSrc);
                  }
                });
              }
              if (img.hasAttribute("data-src")) {
                img.setAttribute("data-src", imageSrc);
              }
            }

            slide.removeAttribute("hidden");
            slide.setAttribute("aria-hidden", index === 0 ? "false" : "true");
          });

          allSlides = slideshowSlides.querySelectorAll("slideshow-slide");
          allSlides.forEach((slide, index) => {
            if (index >= productData.images.length) {
              slide.setAttribute("hidden", "");
              slide.setAttribute("aria-hidden", "true");
            }
          });

          const visibleSlides = Array.from(slideshowSlides.querySelectorAll("slideshow-slide")).filter((slide) => !slide.hasAttribute("hidden"));
          const slideshow = gallery.querySelector("slideshow-component");
          const slideshowArrows = slideshow?.querySelector("slideshow-arrows");

          if (slideshowArrows) {
            if (visibleSlides.length <= 1) {
              slideshowArrows.style.display = "none";
            } else {
              slideshowArrows.style.display = "";
            }
          }

          // Forcer le slideshow à mettre à jour ses refs pour prendre en compte les slides ajoutées
          if (slideshow && typeof slideshow.updatedCallback === "function") {
            slideshow.updatedCallback();
          }

          this.#resetGalleryToFirstSlide(gallery);
          console.log("Gallery updated with complementary product images");
        }
      }

      // Mettre à jour le quick-add
      const quickAdd = productCard.querySelector("quick-add-component");
      if (quickAdd) {
        const variantInput = quickAdd.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = selectedVariant.id;
        }

        // Si le quick-add a une méthode updateVariants, mettre à jour avec tous les variants
        if (typeof quickAdd.updateVariants === "function" && productData.variants.length > 1) {
          const sizeOptionIndex = this.findSizeOptionIndex(productData.options);
          if (sizeOptionIndex >= 0) {
            quickAdd.updateVariants(productData.variants, selectedVariant.id, sizeOptionIndex);
          }
        }
      }

      // Vérifier si le produit est disponible (au moins un variant disponible)
      const isAvailable = productData.variants && productData.variants.some((v) => v.available === true);

      // Mettre à jour la visibilité du quick-add selon la disponibilité (ne pas toucher à la product-card)
      try {
        if (quickAdd) quickAdd.setAttribute("data-available", isAvailable ? "true" : "false");
      } catch (e) {}

      // Calculer allVariantsPreorder depuis les variants
      // Note: les metafields ne sont pas disponibles dans les données JSON de Shopify
      // On ne peut pas vérifier preorder_date, donc on ne peut pas déterminer allVariantsPreorder
      const allVariantsPreorder = false;

      // Préparer les données pour updateBadges
      const badgeData = {
        available: isAvailable,
        allVariantsPreorder: allVariantsPreorder,
        badge: null, // Les metafields ne sont pas disponibles dans JSON
        newBadge: null,
        variants: productData.variants,
      };

      // Mettre à jour les badges
      this.updateBadges(productCard, badgeData);
    } catch (error) {
      console.error("Erreur lors de la mise à jour depuis JSON:", error);
    }
  }

  /**
   * Met à jour les badges de la carte produit
   * @param {HTMLElement} productCard - L'élément de la carte produit
   * @param {Object} productData - Les données du produit (depuis preloaded ou JSON)
   */
  updateBadges(productCard, productData) {
    // Fonction helper pour trouver les traductions
    const findTranslation = (key, fallback) => {
      // Chercher dans window.theme.strings si disponible
      if (window.theme?.strings?.[key]) return window.theme.strings[key];
      // Chercher dans le DOM avec data-translate
      const el = document.querySelector(`[data-translate="${key}"]`);
      if (el) return el.textContent.trim();
      return fallback;
    };

    // Vérifier si le produit est disponible (au moins un variant disponible)
    const isAvailable = productData.variants && productData.variants.some((v) => v.available === true);

    // Calculer si tous les variants sont en précommande
    let allVariantsPreorder = false;
    if (productData.allVariantsPreorder !== undefined) {
      // Utiliser la valeur précalculée depuis les données préchargées
      allVariantsPreorder = productData.allVariantsPreorder;
    } else if (productData.variants && productData.variants.length > 0) {
      // Calculer depuis les variants si disponible
      allVariantsPreorder = productData.variants.every((v) => {
        // Vérifier si le variant a une date de précommande
        return v.preorder_date != null && v.preorder_date !== "";
      });
    }

    // Déterminer le texte du badge personnalisé
    const badgeText = productData.newBadge || productData.badge || null;

    // ===== Mise à jour de .card__badge =====
    const badgeContainers = productCard.querySelectorAll(".card__badge");
    if (badgeContainers.length > 0) {
      let badgeHTML = "";

      // Badge système (épuisé ou précommande) - un seul à la fois
      if (!isAvailable) {
        const soldOutText = findTranslation("products.product.sold_out", "Épuisé");
        const badgeColorScheme = window.theme?.settings?.sold_out_badge_color_scheme || "accent-1";
        badgeHTML = `<span class="badge badge--bottom-left color-${badgeColorScheme}">${soldOutText}</span>`;
      } else if (allVariantsPreorder) {
        const preorderText = findTranslation("products.product.preorder_badge", "Précommande");
        const badgeColorScheme = window.theme?.settings?.sale_badge_color_scheme || "accent-1";
        badgeHTML = `<span class="badge badge--bottom-left color-${badgeColorScheme}">${preorderText}</span>`;
      }

      // Badge personnalisé (peut coexister avec le badge système ou être seul)
      if (badgeText) {
        const badgeColorScheme = window.theme?.settings?.sale_badge_color_scheme || "accent-1";
        badgeHTML += `<span class="badge badge--bottom-left color-${badgeColorScheme}">${badgeText}</span>`;
      }

      // Mettre à jour chaque conteneur de badge
      badgeContainers.forEach((badgeContainer) => {
        badgeContainer.innerHTML = badgeHTML;
      });
    }

    // ===== Mise à jour de .product-badges =====
    const productBadgesContainers = productCard.querySelectorAll(".product-badges");
    if (productBadgesContainers.length > 0) {
      productBadgesContainers.forEach((productBadgesContainer) => {
        // Mettre à jour la classe CSS selon la disponibilité
        if (!isAvailable) {
          productBadgesContainer.classList.add("product-badges--sold-out");
        } else {
          productBadgesContainer.classList.remove("product-badges--sold-out");
        }

        // Construire le HTML selon la logique Liquid
        // Afficher le badge seulement si : non disponible OU tous en précommande
        let innerHTML = "";
        if (!isAvailable || allVariantsPreorder) {
          innerHTML = '<div class="tr-flex"><div class="product-badges__badge">';

          if (!isAvailable) {
            const soldOutText = findTranslation("products.product.sold_out", "Épuisé");
            innerHTML += soldOutText;
          } else if (allVariantsPreorder) {
            const preorderText = findTranslation("products.product.preorder_badge", "Précommande");
            innerHTML += preorderText;
          } else if (badgeText) {
            // Badge personnalisé (affiché seulement si produit épuisé ou précommande)
            innerHTML += badgeText;
          }

          innerHTML += "</div></div>";
        }

        productBadgesContainer.innerHTML = innerHTML;
      });
    }
  }

  async updateCardElements(productCard, { variant, product, productHandle, variantsForColor, sizeOptionIndex }) {
    // Mettre à jour le quick-add avec les variants de la couleur sélectionnée
    const quickAdd = productCard.querySelector("quick-add-component");
    if (quickAdd && variantsForColor && variantsForColor.length > 0 && sizeOptionIndex >= 0) {
      // Utiliser la méthode updateVariants du quick-add component
      if (typeof quickAdd.updateVariants === "function") {
        quickAdd.updateVariants(variantsForColor, variant.id, sizeOptionIndex);
      } else {
        // Fallback : mettre à jour juste l'input hidden
        const variantInput = quickAdd.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
        }
      }
    }

    // Mettre à jour les images (card-gallery) avec l'image du variant sélectionné
    // Pour use_variant_switch : garder uniquement l'image du variant, supprimer les autres
    const gallery = productCard.querySelector(".card-gallery");
    if (gallery) {
      // Récupérer l'image du variant
      let variantImage = null;

      if (variant.featured_image) {
        variantImage = variant.featured_image;
      } else if (variant.image && typeof variant.image === "string" && variant.image.startsWith("http")) {
        variantImage = variant.image;
      } else if (variant.featured_media && variant.featured_media.src) {
        variantImage = variant.featured_media.src;
      } else if (variant.image_id) {
        // Si on a seulement l'ID, chercher l'image dans product.images
        const imageObj = product.images?.find((img) => img.id === variant.image_id);
        if (imageObj) {
          variantImage = imageObj.src || imageObj;
        }
      }

      if (variantImage) {
        console.log("Updating gallery with variant image only:", variantImage);

        // Récupérer toutes les slides
        const slideshowSlides = gallery.querySelector("slideshow-slides");
        if (slideshowSlides) {
          const allSlides = slideshowSlides.querySelectorAll("slideshow-slide");
          console.log("Found", allSlides.length, "slides in gallery");

          // Normaliser les URLs pour la comparaison
          const normalizeUrl = (url) => {
            if (!url) return "";
            return url.split("?")[0].split("#")[0];
          };

          const variantImageNormalized = normalizeUrl(variantImage);
          let firstProductSlideUpdated = false;
          const firstSlideIsCustom = allSlides[0] && this.#isCustomFirstSlide(allSlides[0]);
          const productSlideOffset = firstSlideIsCustom ? 1 : 0;

          // Parcourir toutes les slides
          allSlides.forEach((slide, index) => {
            if (this.#isCustomFirstSlide(slide)) {
              slide.removeAttribute("hidden");
              slide.setAttribute("aria-hidden", "false");
              return;
            }

            const img = slide.querySelector("img");
            if (!img) return;

            const currentImageSrc = img.src || img.getAttribute("src") || "";
            const currentNormalized = normalizeUrl(currentImageSrc);

            if (index === productSlideOffset || currentNormalized === variantImageNormalized) {
              // Première slide produit ou slide avec la même image : mettre à jour et rendre visible
              if (currentNormalized !== variantImageNormalized) {
                img.src = variantImage;
                img.srcset = `${variantImage} 1x`;
                img.loading = "eager";
                img.setAttribute("src", variantImage);

                const picture = slide.querySelector("picture");
                if (picture) {
                  const sources = picture.querySelectorAll("source");
                  sources.forEach((source) => {
                    if (source.srcset) {
                      source.srcset = source.srcset.replace(/https?:\/\/[^\s,]+/g, variantImage);
                    }
                  });
                }

                if (img.hasAttribute("data-src")) {
                  img.setAttribute("data-src", variantImage);
                }
              }

              slide.removeAttribute("hidden");
              slide.setAttribute("aria-hidden", "false");
              firstProductSlideUpdated = true;
            } else {
              slide.setAttribute("hidden", "");
              slide.setAttribute("aria-hidden", "true");
            }
          });

          if (!firstProductSlideUpdated && allSlides.length > productSlideOffset) {
            const firstProductSlide = allSlides[productSlideOffset];
            const img = firstProductSlide.querySelector("img");
            if (img) {
              img.src = variantImage;
              img.srcset = `${variantImage} 1x`;
              img.loading = "eager";
              img.setAttribute("src", variantImage);

              const picture = firstProductSlide.querySelector("picture");
              if (picture) {
                const sources = picture.querySelectorAll("source");
                sources.forEach((source) => {
                  if (source.srcset) {
                    source.srcset = source.srcset.replace(/https?:\/\/[^\s,]+/g, variantImage);
                  }
                });
              }

              if (img.hasAttribute("data-src")) {
                img.setAttribute("data-src", variantImage);
              }

              firstProductSlide.removeAttribute("hidden");
              firstProductSlide.setAttribute("aria-hidden", "false");
            }
          }

          // Compter les slides visibles et masquer/afficher les flèches en conséquence
          const visibleSlides = Array.from(allSlides).filter((slide) => !slide.hasAttribute("hidden"));
          const slideshow = gallery.querySelector("slideshow-component");
          const slideshowArrows = slideshow?.querySelector("slideshow-arrows");

          if (slideshowArrows) {
            if (visibleSlides.length <= 1) {
              // Masquer les flèches s'il n'y a qu'une seule slide visible
              slideshowArrows.style.display = "none";
            } else {
              // Afficher les flèches s'il y a plusieurs slides visibles
              slideshowArrows.style.display = "";
            }
          }

          this.#resetGalleryToFirstSlide(gallery);
          console.log("Gallery updated: only variant image visible");
        }
      } else {
        console.warn("Variant has no image (featured_image or image)");
      }
    }

    // Mettre à jour le prix
    const priceContainer = productCard.querySelector(".product-card__price");
    if (priceContainer && variant.price) {
      const formattedPrice = this.formatMoney(variant.price);
      priceContainer.querySelectorAll(".price-item--regular, .price__sale .price-item").forEach((el) => {
        el.textContent = formattedPrice;
      });

      const hasCompare = variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price);
      const priceEl = priceContainer.querySelector(".price");
      if (priceEl) {
        if (hasCompare) {
          priceEl.classList.add("price--on-sale");
          const compareEl = priceContainer.querySelector(".compare-at-price");
          if (compareEl) {
            compareEl.textContent = this.formatMoney(variant.compare_at_price);
            compareEl.style.display = "";
          }
        } else {
          priceEl.classList.remove("price--on-sale");
          const compareEl = priceContainer.querySelector(".compare-at-price");
          if (compareEl) compareEl.style.display = "none";
        }
      }
    }

    // Mettre à jour l'URL du titre
    const titleLink = productCard.querySelector(".product-card__title");
    if (titleLink) {
      titleLink.href = `/products/${productHandle}?variant=${variant.id}`;
    }

    // Mettre à jour la visibilité du quick-add selon la disponibilité (ne pas toucher à la product-card)
    try {
      const quickAdd = productCard.querySelector("quick-add-component");
      if (quickAdd) quickAdd.setAttribute("data-available", variant.available === true ? "true" : "false");
    } catch (e) {}
  }

  /**
   * Formate un prix en centimes vers le format monétaire
   */
  formatMoney(cents) {
    if (cents == null) return "";
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || "{{amount_with_comma_separator}} €");
    }
    // Fallback avec Intl.NumberFormat avec décimales
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  async updateCardFromHTML(oldCard, newCard) {
    // Extraire les éléments de la nouvelle card
    const newGallery = newCard.querySelector(".card-gallery");
    const newBadge = newCard.querySelector(".product-badges");
    const newTitle = newCard.querySelector(".product-card__title");
    const newPrice = newCard.querySelector(".product-card__price");
    const newQuickAdd = newCard.querySelector("quick-add-component");

    // Remplacer les éléments dans l'ancienne card avec gestion de la réinitialisation des composants

    // 1. Mettre à jour la galerie (images) - Pour use_complementary_switch : remplacer toutes les images
    if (newGallery) {
      const oldGallery = oldCard.querySelector(".card-gallery");
      if (oldGallery) {
        console.log("Replacing gallery with new product images");

        // Cloner profondément pour éviter les références partagées
        const clonedGallery = newGallery.cloneNode(true);

        // Remplacer l'ancienne galerie par la nouvelle
        oldGallery.replaceWith(clonedGallery);

        // Réinitialiser le slideshow component si nécessaire
        // Le composant se réinitialisera automatiquement via connectedCallback
        // Mais on peut forcer la réinitialisation si le composant existe déjà
        const slideshow = clonedGallery.querySelector("slideshow-component");
        if (slideshow) {
          // Le composant custom element se reconnectera automatiquement
          // Si besoin, on peut déclencher manuellement la réinitialisation
          if (slideshow.connectedCallback && typeof slideshow.connectedCallback === "function") {
            // Ne pas appeler directement, laisser le navigateur le faire
          }
        }

        console.log("Gallery replaced successfully");
      }
    }

    // 2. Mettre à jour les badges
    if (newBadge) {
      const oldBadge = oldCard.querySelector(".product-badges");
      if (oldBadge) {
        oldBadge.replaceWith(newBadge.cloneNode(true));
      }
    }

    // 3. Mettre à jour le titre
    if (newTitle) {
      const oldTitle = oldCard.querySelector(".product-card__title");
      if (oldTitle) {
        const clonedTitle = newTitle.cloneNode(true);
        oldTitle.replaceWith(clonedTitle);
      }
    }

    // 4. Mettre à jour le prix
    if (newPrice) {
      const oldPrice = oldCard.querySelector(".product-card__price");
      if (oldPrice) {
        oldPrice.replaceWith(newPrice.cloneNode(true));
      }
    }

    // 5. Mettre à jour le quick-add (nécessite une attention particulière pour réinitialiser le composant)
    if (newQuickAdd) {
      const oldQuickAdd = oldCard.querySelector("quick-add-component");
      if (oldQuickAdd) {
        // Sauvegarder les refs si nécessaire
        const clonedQuickAdd = newQuickAdd.cloneNode(true);

        // Remplacer l'ancien composant
        oldQuickAdd.replaceWith(clonedQuickAdd);

        // Le composant se réinitialisera automatiquement via connectedCallback
        // Mais on peut forcer la réinitialisation si nécessaire
        if (clonedQuickAdd.connectedCallback) {
          clonedQuickAdd.connectedCallback();
        }
      }
    }
  }
}

if (!customElements.get("switch-color-component")) {
  customElements.define("switch-color-component", SwitchColorComponent);
  try {
    if (window?.localStorage?.getItem("debugSwitchColor") === "1") {
      console.log("[SwitchColor] Composant enregistré. Debug activé.");
    }
  } catch (e) {}
} else {
  try {
    if (window?.localStorage?.getItem("debugSwitchColor") === "1") {
      console.log("[SwitchColor] Composant déjà enregistré.");
    }
  } catch (e) {}
}
